import { Collapsible } from "@/design-system/Collapsible/Collapsible";
import { cn } from "@/lib/utils";
import type { SectionTier } from "@repo/shared/hooks";
import { comparePlayerKey } from "@repo/shared/redux";
import { DetailedPlayer } from "@repo/shared/types";

import { ColumnDividers } from "./ColumnDividers";
import { CompareSectionHeader } from "./CompareSectionHeader";
import { Mode } from "./ModeToggle";

/** Zebra background for alternating compare rows/blocks. */
export const zebra = (index: number) =>
  index % 2 ? "bg-[#242424]" : "bg-[#1c1c1c]";

/**
 * Winner-aware styling for a compared value: dimmed when unknown, winner green
 * when it wins its row, neutral otherwise (Attributes + Info cells).
 */
export const compareValueClass = (value: unknown, winner: boolean) =>
  value == null
    ? "font-medium text-neutral-600"
    : winner
      ? "font-bold text-winner"
      : "font-semibold text-neutral-400";

/**
 * The chrome every compare section shares: the collapsible boxed header (title
 * + optional endpoint tier row) and the bordered content box with the
 * full-height column dividers.
 */
export const CompareSectionShell = ({
  title,
  count,
  gridStyle,
  tiers,
  open,
  onToggle,
  mode,
  pinnedWidth,
  headerClassName,
  children,
}: {
  title: string;
  /** Number of compared columns (drives the dividers). */
  count: number;
  gridStyle: React.CSSProperties;
  /** Per-player tier + winner for the header's tier row, when the endpoint grades the section. */
  tiers?: SectionTier[];
  open: boolean;
  onToggle: () => void;
  mode: Mode;
  /** Mobile: sticky-left width for the pinned rows inside the shared scroller. */
  pinnedWidth?: number;
  headerClassName?: string;
  children: React.ReactNode;
}) => (
  <section>
    <CompareSectionHeader
      title={title}
      open={open}
      onToggle={onToggle}
      gridStyle={gridStyle}
      tiers={tiers}
      className={headerClassName}
      pinnedWidth={pinnedWidth}
      hideTitle={mode === "compact"}
    />
    <Collapsible open={open}>
      {/* overflow-clip (not hidden) so the pinned rows can stick to the outer
          horizontal scroller on mobile — where the box also goes full-bleed
          (side borders would push the pinned rows 1px past the columns into a
          phantom x-scroll). */}
      <div
        className={cn(
          "relative overflow-clip border-b border-neutral-800",
          pinnedWidth == null && "rounded-b border-l border-r",
          // Compact with no tiers: the header is gone entirely, so the box
          // supplies its own top edge and spacing.
          mode === "compact" &&
            !tiers?.some((t) => t.tier) &&
            cn("border-t", pinnedWidth == null && "rounded-t"),
        )}
      >
        <ColumnDividers count={count} />
        {children}
      </div>
    </Collapsible>
  </section>
);

/**
 * Sticky category/position header row inside a section (list mode). Pinned
 * (mobile): w-full would beat the inline pinned width (Tailwind runs with
 * important:true), so it's swapped out instead of overridden.
 */
export const ComparePinnedRowHeader = ({
  label,
  pinnedWidth,
}: {
  label: string;
  pinnedWidth?: number;
}) => (
  <div
    className={cn(
      "flex items-center border-b border-neutral-800 bg-[#181818] px-[14px] py-[10px]",
      pinnedWidth != null ? "sticky left-0" : "w-full",
    )}
    style={pinnedWidth != null ? { width: pinnedWidth } : undefined}
  >
    <span className="text-xs font-bold uppercase tracking-[0.1em] text-neutral-500">
      {label}
    </span>
  </div>
);

/** One zebra data row on the shared column axis. */
export const CompareRow = ({
  index,
  gridStyle,
  className,
  children,
}: {
  /** Row index within its block — drives the zebra striping. */
  index: number;
  gridStyle: React.CSSProperties;
  className?: string;
  children: React.ReactNode;
}) => (
  <div
    className={cn(
      "grid items-center border-b border-neutral-800 py-[6px]",
      zebra(index),
      className,
    )}
    style={gridStyle}
  >
    {children}
  </div>
);

/** Dense per-player flex-wrap cell (grouped/compact modes). */
export const CompareWrapCell = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <div className="flex flex-wrap content-start justify-center gap-[4px] px-2">
    {children}
  </div>
);

/**
 * Grouped mode's zebra block: the label repeated over every column, then one
 * dense wrap cell per player.
 */
export const CompareGroupedBlock = ({
  label,
  index,
  gridStyle,
  players,
  renderPlayer,
}: {
  label: string;
  /** Block index within the section — drives the zebra striping. */
  index: number;
  gridStyle: React.CSSProperties;
  players: DetailedPlayer[];
  renderPlayer: (player: DetailedPlayer) => React.ReactNode;
}) => (
  <div
    className={cn(
      "border-b border-neutral-800 py-[9px] last:border-b-0",
      zebra(index),
    )}
  >
    <div className="mb-[8px] grid" style={gridStyle}>
      {players.map((player) => (
        <div
          key={comparePlayerKey(player)}
          className="text-center text-xs font-bold uppercase text-neutral-500"
        >
          {label}
        </div>
      ))}
    </div>
    <div className="grid" style={gridStyle}>
      {players.map((player) => (
        <CompareWrapCell key={comparePlayerKey(player)}>
          {renderPlayer(player)}
        </CompareWrapCell>
      ))}
    </div>
  </div>
);

/** Compact mode: one dense wrap cell per player, no grouping. */
export const CompareCompactRow = ({
  gridStyle,
  players,
  renderPlayer,
}: {
  gridStyle: React.CSSProperties;
  players: DetailedPlayer[];
  renderPlayer: (player: DetailedPlayer) => React.ReactNode;
}) => (
  <div className="grid bg-[#1c1c1c] py-[9px]" style={gridStyle}>
    {players.map((player) => (
      <CompareWrapCell key={comparePlayerKey(player)}>
        {renderPlayer(player)}
      </CompareWrapCell>
    ))}
  </div>
);
